// Standard error contract shared by the whole Brothers Club app.
// Nothing in here may ever carry OTPs, tokens, keys or payment credentials —
// redaction is enforced in ./redact before anything is persisted.

export type DiagCategory =
  | "startup"
  | "auth"
  | "network"
  | "api"
  | "database"
  | "wallet"
  | "bet"
  | "result"
  | "admin"
  | "upload"
  | "notification"
  | "unknown";

export type DiagSeverity = "info" | "warning" | "error" | "critical";

/** Whether the user's money moved. Never assume "not_charged". */
export type WalletImpact =
  | "not_applicable"
  | "not_charged"
  | "charged"
  | "unknown"
  | "checking";

export type SafePrimitive = string | number | boolean | null;

export type DiagnosticEvent = {
  error_id: string;
  category: DiagCategory;
  code: string;
  safe_message: string;
  technical_message?: string;
  page?: string;
  route?: string;
  stage?: string;
  request_id?: string;
  user_id?: string;
  app_version?: string;
  build_id?: string;
  platform?: string;
  duration_ms?: number;
  retryable: boolean;
  severity: DiagSeverity;
  wallet_impact?: WalletImpact;
  group_hash?: string;
  metadata?: Record<string, SafePrimitive | SafePrimitive[] | Record<string, unknown>>;
  created_at: string;
};

// ---------------------------------------------------------------------------
// Error codes
// ---------------------------------------------------------------------------

export const DIAG_CODES = {
  // network / api
  offline: "offline",
  dns_failure: "dns_failure",
  timeout: "timeout",
  cors_blocked: "cors_blocked",
  unauthorized: "unauthorized",
  forbidden: "forbidden",
  not_found: "not_found",
  validation_failed: "validation_failed",
  rate_limited: "rate_limited",
  server_error: "server_error",
  malformed_response: "malformed_response",
  network_failure: "network_failure",

  // auth / otp
  otp_send_failed: "otp_send_failed",
  otp_invalid: "otp_invalid",
  otp_expired: "otp_expired",
  account_lookup_failed: "account_lookup_failed",
  account_create_failed: "account_create_failed",
  magic_link_failed: "magic_link_failed",
  missing_token_hash: "missing_token_hash",
  session_exchange_failed: "session_exchange_failed",
  session_save_failed: "session_save_failed",

  // bet / wallet
  market_closed: "market_closed",
  invalid_limit: "invalid_limit",
  insufficient_balance: "insufficient_balance",
  duplicate_request: "duplicate_request",
  wallet_lock_timeout: "wallet_lock_timeout",
  database_timeout: "database_timeout",
  bet_insert_failed: "bet_insert_failed",
  transaction_rolled_back: "transaction_rolled_back",
  request_still_processing: "request_still_processing",

  // startup / runtime
  startup_slow: "startup_slow",
  startup_failed: "startup_failed",
  bundle_stale: "bundle_stale",
  plugin_unavailable: "plugin_unavailable",
  render_failed: "render_failed",
  unhandled_error: "unhandled_error",
  unhandled_rejection: "unhandled_rejection",
  upload_failed: "upload_failed",
  push_registration_failed: "push_registration_failed",
  unknown: "unknown",
} as const;

export type DiagCode = (typeof DIAG_CODES)[keyof typeof DIAG_CODES];

/** Codes that are safe to retry without risking a double charge. */
export const RETRYABLE_CODES = new Set<string>([
  DIAG_CODES.offline,
  DIAG_CODES.dns_failure,
  DIAG_CODES.timeout,
  DIAG_CODES.network_failure,
  DIAG_CODES.rate_limited,
  DIAG_CODES.server_error,
  DIAG_CODES.otp_send_failed,
  DIAG_CODES.startup_slow,
  DIAG_CODES.bundle_stale,
  DIAG_CODES.database_timeout,
  DIAG_CODES.upload_failed,
]);

// ---------------------------------------------------------------------------
// User-facing safe messages. Never expose raw JS/HTTP errors.
// ---------------------------------------------------------------------------

const SAFE_MESSAGES: Record<string, string> = {
  [DIAG_CODES.offline]: "You appear to be offline. Please check your connection and retry.",
  [DIAG_CODES.dns_failure]: "Connection is temporarily unavailable. Please retry.",
  [DIAG_CODES.network_failure]: "Connection is temporarily unavailable. Please retry.",
  [DIAG_CODES.timeout]: "The request took too long. Please retry.",
  [DIAG_CODES.cors_blocked]: "The app could not reach the server. Please retry.",
  [DIAG_CODES.unauthorized]: "Your session has expired. Please sign in again.",
  [DIAG_CODES.forbidden]: "You do not have permission for this action.",
  [DIAG_CODES.not_found]: "That item is no longer available.",
  [DIAG_CODES.validation_failed]: "Some details were not accepted. Please check and try again.",
  [DIAG_CODES.rate_limited]: "Too many attempts. Please wait a moment and retry.",
  [DIAG_CODES.server_error]: "The service is temporarily unavailable. Please retry.",
  [DIAG_CODES.malformed_response]: "The service returned an unexpected response. Please retry.",

  [DIAG_CODES.otp_send_failed]: "OTP could not be sent. Please retry.",
  [DIAG_CODES.otp_invalid]: "That OTP is incorrect. Please re-enter it.",
  [DIAG_CODES.otp_expired]: "That OTP has expired. Please request a new one.",
  [DIAG_CODES.account_lookup_failed]: "Login could not be completed. Please retry.",
  [DIAG_CODES.account_create_failed]: "Account could not be created. Please retry.",
  [DIAG_CODES.magic_link_failed]: "Login could not be completed. Please retry.",
  [DIAG_CODES.missing_token_hash]: "Login could not be completed. Please retry.",
  [DIAG_CODES.session_exchange_failed]: "Login could not be completed. Please retry.",
  [DIAG_CODES.session_save_failed]: "Login could not be saved on this device. Please retry.",

  [DIAG_CODES.market_closed]: "This market is closed for betting.",
  [DIAG_CODES.invalid_limit]: "This bet is outside the allowed limits for this market.",
  [DIAG_CODES.insufficient_balance]: "Your balance is not enough for this bet. Your wallet was not charged.",
  [DIAG_CODES.duplicate_request]: "This bet was already submitted. It was not charged twice.",
  [DIAG_CODES.wallet_lock_timeout]: "Your bet could not be confirmed right now. We are checking your wallet status.",
  [DIAG_CODES.database_timeout]: "The service is busy. Please retry.",
  [DIAG_CODES.bet_insert_failed]: "Bet could not be submitted.",
  [DIAG_CODES.transaction_rolled_back]: "Bet could not be submitted. Your wallet was not charged.",
  [DIAG_CODES.request_still_processing]: "Your bet is still being processed. Please check My Bets shortly.",

  [DIAG_CODES.startup_slow]: "The app is taking longer than usual to start.",
  [DIAG_CODES.startup_failed]: "The app could not start. Please retry.",
  [DIAG_CODES.bundle_stale]: "An app update is available. Please restart the app.",
  [DIAG_CODES.plugin_unavailable]: "This feature is not available on your app version. Please update.",
  [DIAG_CODES.render_failed]: "This screen could not be displayed. Please retry.",
  [DIAG_CODES.unhandled_error]: "Something went wrong. Please retry.",
  [DIAG_CODES.unhandled_rejection]: "Something went wrong. Please retry.",
  [DIAG_CODES.upload_failed]: "The file could not be uploaded. Please retry.",
  [DIAG_CODES.push_registration_failed]: "Notifications could not be enabled on this device.",
  [DIAG_CODES.unknown]: "Something went wrong. Please retry.",
};

const CATEGORY_FALLBACK: Record<DiagCategory, string> = {
  startup: "The app could not start.",
  auth: "Login could not be completed.",
  network: "Connection is temporarily unavailable. Please retry.",
  api: "Connection is temporarily unavailable. Please retry.",
  database: "The service is temporarily unavailable. Please retry.",
  wallet: "This wallet action could not be completed.",
  bet: "Bet could not be submitted.",
  result: "Results could not be loaded right now.",
  admin: "This admin action could not be completed.",
  upload: "The file could not be uploaded. Please retry.",
  notification: "Notifications could not be updated.",
  unknown: "Something went wrong. Please retry.",
};

export function safeMessageFor(category: DiagCategory, code: string): string {
  return SAFE_MESSAGES[code] ?? CATEGORY_FALLBACK[category] ?? CATEGORY_FALLBACK.unknown;
}

export function isRetryable(code: string): boolean {
  return RETRYABLE_CODES.has(code);
}

// ---------------------------------------------------------------------------
// Performance thresholds (ms): [slow, critical]
// ---------------------------------------------------------------------------

export type PerfMetricName =
  | "android_cold_start"
  | "first_visible_frame"
  | "auth_restore"
  | "home_ready"
  | "games_load"
  | "wallet_load"
  | "bet_screen_ready"
  | "bet_submit"
  | "history_load"
  | "admin_dashboard_load"
  | "result_settlement"
  | "upload";

export const PERF_THRESHOLDS: Record<PerfMetricName, [number, number]> = {
  android_cold_start: [2500, 5000],
  first_visible_frame: [1200, 2500],
  auth_restore: [800, 2000],
  home_ready: [2000, 4000],
  games_load: [1200, 3000],
  wallet_load: [1200, 3000],
  bet_screen_ready: [1200, 3000],
  bet_submit: [2500, 6000],
  history_load: [1500, 3500],
  admin_dashboard_load: [2500, 6000],
  result_settlement: [5000, 15000],
  upload: [5000, 15000],
};

export type PerfRating = "healthy" | "slow" | "critical";

export function rateDuration(metric: PerfMetricName, ms: number): PerfRating {
  const [slow, critical] = PERF_THRESHOLDS[metric] ?? [2000, 5000];
  if (ms >= critical) return "critical";
  if (ms >= slow) return "slow";
  return "healthy";
}
